import classnames from 'classnames';
import type * as React from 'react';

import { ErrorIcon, InfoIcon } from '../Icon';
import Tooltip from '../Tooltip';
import { RequiredStateText } from '../RequiredStateText';
import { normalizeTestId } from '../../utils';
import Radio from './Radio';
import './Radio.css';

export interface RadioOption<T extends string | boolean = string> {
  label: string;
  value: T;
  disabled?: boolean;
}

export interface RadioGroupProps<T extends string | boolean = string> {
  /**
   * Name attribute for the radio group
   */
  name: string;
  /**
   * Label for the radio group
   */
  label?: string;
  /**
   * Options to display as radio buttons
   */
  options: RadioOption<T>[];
  /**
   * Currently selected value
   */
  value?: T;
  /**
   * Callback when radio value changes - receives the actual typed value
   */
  onChange?: (value: T) => void;
  /**
   * Additional class name
   */
  className?: string;
  /**
   * Whether the field is required
   */
  required?: boolean;
  /**
   * Tooltip info text to display next to the label
   */
  tooltipText?: string;
  /**
   * Error message to display
   */
  errorMessage?: string;
  /**
   * Display radios inline (horizontally) instead of stacked
   */
  inline?: boolean;
  /**
   * Test ID for automated testing
   */
  'data-testid'?: string;
}

function RadioGroup<T extends string | boolean = string>({
  name,
  label,
  options,
  value,
  onChange,
  className,
  required,
  tooltipText,
  errorMessage,
  inline = false,
  'data-testid': dataTestId,
}: RadioGroupProps<T>) {
  const groupClasses = classnames('access-radio-group', className, {
    'access-radio-group--error': errorMessage,
    'access-radio-group--inline': inline,
  });
  const baseTestId = dataTestId ?? 'radio-group';

  const tooltipId = `${name}-tooltip`;
  const errorId = `${name}-error`;
  const labelTextId = `${name}-label-text`;
  const requiredTextId = `${name}-required`;

  /**
   * ACP-2092 / ACP-2077 (Section 508): the error state lives on the radios, not on the
   * <fieldset>. A group exposes neither aria-invalid nor aria-required in the
   * accessibility tree, so a required radio group reported nothing at all — the workflow
   * and the Add User modal both look for `[aria-invalid="true"]` to announce and to move
   * focus, and never found it. Measured on the Add User modal: an empty submit announced
   * "4 fields need your attention" on a form with five required fields.
   *
   * Name the group from the label text alone. A <legend>
   * contributes its whole subtree, so the tooltip trigger's aria-label was folded in and
   * the group announced as "... do you still receive this pay required Select one." —
   * the same leak #492 removed from every other control.
   */
  const labelledBy = label ? `${labelTextId}${required ? ` ${requiredTextId}` : ''}` : undefined;

  return (
    <fieldset
      aria-describedby={errorMessage ? errorId : undefined}
      aria-labelledby={labelledBy}
      className={groupClasses}
      data-testid={baseTestId}>
      {label && (
        <legend
          className='access-radio-group__legend'
          data-testid={`${baseTestId}-label`}>
          <div className='access-radio-group__label-wrapper'>
            <span className='ds-c-label'>
              <span className='access-radio-group__label-content'>
                <span id={labelTextId}>{label}</span>
                {(required || tooltipText) && (
                  <span className='access-radio-group__label-suffix'>
                    {required && (
                      <>
                        <span
                          aria-hidden='true'
                          className='access-radio-group__required-asterisk'>
                          *
                        </span>
                        <RequiredStateText id={requiredTextId} />
                      </>
                    )}
                    {tooltipText && (
                      <Tooltip
                        className='access-radio-group__info-tooltip'
                        id={tooltipId}
                        placement='top'
                        title={tooltipText}>
                        <InfoIcon />
                      </Tooltip>
                    )}
                  </span>
                )}
              </span>
            </span>
            <div
              className='access-radio-group__error-icon-wrapper'
              data-testid={`${baseTestId}-error-icon-wrapper`}>
              {errorMessage ? (
                <>
                  <span
                    className='ds-u-visibility--screen-reader'
                    id={errorId}
                    role='alert'>
                    {errorMessage}
                  </span>
                  <Tooltip
                    isError
                    activeClassName='access-radio-group__error-tooltip--active'
                    className='access-radio-group__error-icon'
                    placement='top'
                    title={errorMessage}>
                    <span
                      className='access-radio-group__error-icon-content'
                      role='img'>
                      <ErrorIcon ariaLabel={`Error: ${errorMessage}`} />
                    </span>
                  </Tooltip>
                </>
              ) : (
                <span
                  aria-hidden='true'
                  className='access-radio-group__error-icon-placeholder'
                />
              )}
            </div>
          </div>
        </legend>
      )}
      <div
        className='access-radio-group__options'
        data-testid={`${baseTestId}-options`}>
        {options.map((option) => (
          <Radio
            key={String(option.value)}
            checked={value === option.value}
            data-testid={`${baseTestId}-option-${normalizeTestId(String(option.value))}`}
            describedBy={errorMessage ? errorId : undefined}
            disabled={option.disabled}
            invalid={!!errorMessage}
            label={option.label}
            name={name}
            required={required}
            value={option.value}
            onChange={() => onChange?.(option.value)}
          />
        ))}
      </div>
    </fieldset>
  );
}

export default RadioGroup;
